import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { StatusService } from 'src/app/services/status.service';

@Component({
  selector: 'ap-date',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss']
})
export class DateInputComponent implements OnInit, OnChanges {

  @Input() date: Date | null = null;
  @Input() placeholder: string = 'dd-mm-yy';
  @Input() readonly: boolean = false;

  @Output() dateChange = new EventEmitter<Date | null>();

  displayValue = '';
  hasError = false;

  private rawDigits = '';
  private lastValidDate: Date | null = null;
  private isFocused = false;

  constructor(private statusService: StatusService) {}

  ngOnInit(): void {
    this.syncFromInput();
  }

  ngOnChanges(): void {
    // Only resync from outside when we’re NOT editing
    if (!this.isFocused) {
      this.syncFromInput();
    }
  }

  private syncFromInput(): void {
    if (this.date instanceof Date && !isNaN(this.date.getTime())) {
      this.lastValidDate = this.date;
      this.rawDigits = this.dateToDigits(this.date);
      this.displayValue = this.formatDigits(this.rawDigits);
      this.hasError = false;
      this.announceDate(this.date);
    } else {
      this.lastValidDate = null;
      this.rawDigits = '';
      this.displayValue = '';
      this.hasError = false;
      this.statusService.clear();
    }
  }

  private dateToDigits(date: Date): string {
    const dd = (`0${date.getDate()}`).slice(-2);
    const mm = (`0${date.getMonth() + 1}`).slice(-2);
    const yy = (`0${date.getFullYear() % 100}`).slice(-2);
    return `${dd}${mm}${yy}`;
  }

  private formatDigits(digits: string): string {
    const len = digits.length;
    if (len <= 2) {
      return digits;
    } else if (len <= 4) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    } else {
      return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
    }
  }

  private parseDateFromDigits(digits: string): Date | null {
    if (digits.length !== 6) return null;

    const day = +digits.slice(0, 2);
    const month = +digits.slice(2, 4);
    const year2 = +digits.slice(4, 6);

    const fullYear = year2 <= 49 ? 2000 + year2 : 1900 + year2;

    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;

    const d = new Date(fullYear, month - 1, day);

    if (
      d.getFullYear() !== fullYear ||
      d.getMonth() !== month - 1 ||
      d.getDate() !== day
    ) {
      return null;
    }

    return d;
  }

  private announceDate(date: Date | null): void {
    if (!date) {
      this.statusService.clear();
      return;
    }
    const long = date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    this.statusService.setText(long);
  }

  // === EVENTS ===

onInput(value: string): void {
  if (this.readonly) return;

  this.hasError = false;

  const digits = (value || '').replace(/\D+/g, '').slice(0, 6);
  this.rawDigits = digits;
  this.displayValue = this.formatDigits(this.rawDigits);

  // DO NOT emit here
  // DO NOT modify this.date here

  // Just validate visually if 6 digits
  if (this.rawDigits.length === 6) {
    const parsed = this.parseDateFromDigits(this.rawDigits);
    this.hasError = parsed === null;

    // Still do not emit here.
    // Wait for blur or a confirmed input action.
  } else {
    this.hasError = false;
  }
}

  onFocus(): void {
    this.isFocused = true;
  }

onBlur(): void {
  this.isFocused = false;

  if (!this.rawDigits || this.rawDigits.length !== 6) {
    // Incomplete: no date, no emit.
    this.hasError = true;
    this.announceDate(null);
    return;
  }

  const parsed = this.parseDateFromDigits(this.rawDigits);
  if (parsed) {
    // VALID DATE → Emit
    this.date = parsed;
    this.lastValidDate = parsed;
    this.displayValue = this.formatDigits(this.rawDigits);
    this.dateChange.emit(parsed);
    this.hasError = false;
    this.announceDate(parsed);
  } else {
    // INVALID DATE → No emit
    this.hasError = true;
    this.date = null;
    this.announceDate(null);
  }
}
}